import { ReactNode } from "react";

import Tooltip from "ds/components/Tooltip";
import { TooltipProps, TooltipReferenceProps } from "ds/components/Tooltip/types";
import { BillingTierFeature, BlueprintVersionedGroup } from "types/generated";
import useTierFeature from "views/Account/hooks/useTierFeature";
import { AccessResult } from "utils/access";
import useCanManageTemplate from "shared/Templates/access/useCanManageTemplate";
import useCanCreateTemplate from "shared/Templates/access/useCanCreateTemplate";

import { TemplateActions } from "./types";

type TemplatesFeatureGateTooltipProps = {
  on: (props: TooltipReferenceProps & { isDisabled: boolean }) => ReactNode;
  action: TemplateActions;
  template?: BlueprintVersionedGroup;
  tooltipPlacement?: TooltipProps["placement"];
};

const getTemplateFeatureGatingMessage = (action: TemplatesFeatureGateTooltipProps["action"]) => {
  switch (action) {
    case TemplateActions.CreateTemplate:
      return "Templates are not supported by your plan. Upgrade to the higher plan to be able to create Templates.";
    case TemplateActions.EditTemplate:
      return "Templates are not supported by your plan. Upgrade to the higher plan to be able to edit Templates.";
  }
};

const getTemplateActionAccessResult = (
  action: TemplatesFeatureGateTooltipProps["action"],
  canCreateTemplate: AccessResult,
  canManageTemplate: AccessResult
) => {
  switch (action) {
    case TemplateActions.CreateTemplate:
      return canCreateTemplate;
    case TemplateActions.EditTemplate:
      return canManageTemplate;
  }
};

const TemplateFeatureGateTooltip = ({
  on,
  action,
  template,
  tooltipPlacement,
}: TemplatesFeatureGateTooltipProps) => {
  const isFeatureAvailableInPlan = useTierFeature(BillingTierFeature.Blueprints);
  const canCreateTemplate = useCanCreateTemplate();
  const canManageTemplate = useCanManageTemplate()(template?.space.id);

  const accessResult = getTemplateActionAccessResult(action, canCreateTemplate, canManageTemplate);

  const isDisabled = !isFeatureAvailableInPlan || !accessResult.result;
  const isTooltipActive = !isFeatureAvailableInPlan || !!accessResult.message;

  return (
    <Tooltip
      on={(props) => on({ ...props, isDisabled })}
      active={isTooltipActive}
      widthMode="maxWidthSm"
      placement={tooltipPlacement}
    >
      {isFeatureAvailableInPlan ? accessResult.message : getTemplateFeatureGatingMessage(action)}
    </Tooltip>
  );
};

export default TemplateFeatureGateTooltip;
